import { useCallback, useMemo, useState } from "react"

import { useAnalytics } from "@app/hooks/useAnalytics"
import { useWishlistContext } from "@app/providers/wishlist"

type Options = {
  wishlistId?: string
  isWishlistSharedPage?: boolean
  /** Fired right before an item is added — use it to trigger the add-to-wishlist animation. */
  onAdd?: () => void
  /** GA4 `location` param for add_to_wishlist / remove_from_wishlist: card / quick_add / pdp / cart (Productive #4293). */
  location?: string
}

/**
 * Single source of truth for add/remove behaviour behind every wishlist toggle
 * (PLP, PDP, cart, wishlist pages). Product-shape normalisation lives in the wishlist
 * provider, so any product-ish input — a catalogue product or a cart line's merchandise —
 * can be passed straight through. Sharing this keeps the entry points from drifting apart.
 */
export const useWishlistToggle = (product: any, { wishlistId, isWishlistSharedPage, onAdd, location }: Options = {}) => {
  const { fetched, available, isInWishlist, addToWishlist, removeFromWishlist } = useWishlistContext()
  const { trackWishlistUpdate } = useAnalytics()
  const [loading, setLoading] = useState(false)

  const inWishlist = useMemo(() => isInWishlist(product), [isInWishlist, product])

  const toggle = useCallback(
    async (e?: { preventDefault?: () => void }) => {
      e?.preventDefault?.()
      setLoading(true)
      if (inWishlist) {
        const removed = await removeFromWishlist(product, !isWishlistSharedPage ? wishlistId : undefined)
        if (removed) trackWishlistUpdate("remove", product, location)
      } else {
        onAdd?.()
        const added = await addToWishlist(product)
        if (added) trackWishlistUpdate("add", product, location)
      }
      setLoading(false)
    },
    [addToWishlist, inWishlist, isWishlistSharedPage, location, onAdd, product, removeFromWishlist, trackWishlistUpdate, wishlistId]
  )

  return { fetched, available, inWishlist, loading, toggle }
}
